import { computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { Person, Project } from 'models'
import { PaginationSearchBase } from 'store/PaginationSearchBase'
import { TriggerSearchFunc } from 'store/PaginationSearchBase/PaginationSearchBase.types'
import { PERSON_STORE } from 'store/PersonStore'
import { RootStore } from 'store/RootStore'
import { getSortingResult, isProjectPublished } from 'utilities'
import {
  PersonProjectsTableSortProps,
  PersonProjectsTableSearchProps,
  initialPersonProjectsPaginationProps,
  initialPersonProjectSearchProps
} from './PersonProjectsTableStore.types'

export const PERSON_PROJECTS_TABLE_STORE = 'PERSON_PROJECTS_TABLE_STORE'

export class PersonProjectsTableStore extends PaginationSearchBase<
  PersonProjectsTableSortProps,
  PersonProjectsTableSearchProps,
  Project
> {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    super(
      initialPersonProjectsPaginationProps,
      initialPersonProjectSearchProps,
      [],
      ['name']
    )
    this._rootStore = rootStore
  }

  triggerSearch: TriggerSearchFunc = () => {
    const {
      name,
      type,
      role,
      genre,
      published
    } = this.searchProps

    const results = this.projects
      .filter(project => {
        const nameFilter = name
          ? project.name.toLowerCase().includes(name.trim().toLowerCase())
          : true
        const typeFilter = type
          ? project.type === type
          : true
        const roleFilter = role
          ? this.getRolesByProjectId(project.id).includes(role)
          : true
        const genreFilter = genre
          ? (project.genres || []).includes(genre)
          : true
        const publishedFilter = published as unknown as string !== ''
          ? isProjectPublished(project.status) === published
          : true

        return nameFilter &&
          typeFilter &&
          roleFilter &&
          genreFilter &&
          publishedFilter
      })

    const { sortProp, sortDirection } = this.pagination
    results.sort((a, b) => {
      switch (sortProp) {
        case PersonProjectsTableSortProps.NAME: {
          return getSortingResult(a.name, b.name, sortDirection)
        }
        case PersonProjectsTableSortProps.TYPE: {
          return getSortingResult(a.type, b.type, sortDirection)
        }
        case PersonProjectsTableSortProps.ROLE: {
          return getSortingResult(
            this.getRolesByProjectId(a.id),
            this.getRolesByProjectId(b.id),
            sortDirection
          )
        }
        case PersonProjectsTableSortProps.GENRE: {
          return getSortingResult(
            a.genresAsString, b.genresAsString, sortDirection
          )
        }
        default: return 0
      }
    })

    this.setSearchResults(results)
  }

  @computed get person (): Person {
    return this._rootStore[PERSON_STORE].personToView
  }

  @computed get projects (): Project[] {
    return this.person.projects || []
  }

  @computed get projectsCount (): number {
    // return this.projects.length
    return this.person.rolesByProjectId
      ? this.person.rolesByProjectId.size
      : this.projects.length
  }

  @observable getRolesByProjectId = computedFn(
    (projectId: number): string => {
      return this.person.getRolesByProjectId(projectId)
    }
  )
}
